import { supabase } from './supabase';

export interface MediaItem {
  id: string;
  url: string;
  type: 'foto' | 'video';
  mimeType: string | null;
  wiSfId: string | null;
  woSfId: string | null;
  fase: string;
  actorEmail: string | null;
  takenAt: string;
}

// Klant-zichtbare fasen — MOET gelijk blijven aan KLANT_FASEN in de
// client-app-timeline (api/fotos/[token].ts): de fotonummers ("zie foto 3")
// worden over precies deze set berekend en moeten op beide schermen kloppen.
export const KLANT_FASEN = ['voorinspectie', 'reinigen', 'na-inspectie'];

/**
 * Fotonummer per media-id volgens de timeline-regel: chronologisch (taken_at
 * asc), alleen foto's in klant-fasen. Interne foto's en video's krijgen geen
 * nummer.
 */
export function klantFotoNummers(media: MediaItem[]): Map<string, number> {
  const nummers = new Map<string, number>();
  media
    .filter((m) => m.type === 'foto' && KLANT_FASEN.includes(m.fase))
    .sort((a, b) => a.takenAt.localeCompare(b.takenAt))
    .forEach((m, i) => nummers.set(m.id, i + 1));
  return nummers;
}

export const MEDIA_FASE_LABELS: Record<string, string> = {
  voorinspectie: 'Voorinspectie',
  reinigen: 'Reinigen',
  'na-inspectie': 'Na-inspectie',
  klacht: 'Klacht',
  risico: 'Risico',
  ophaal: 'Ophaal',
  aflevering: 'Aflevering',
  referentie: 'Referentie',
  import: 'Import',
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

/** Eigen media (media-laag) van een WI of WO. Faalt zacht met een lege lijst. */
export async function fetchMedia(doel: { wiSfId?: string; woSfId?: string }): Promise<MediaItem[]> {
  const headers = await authHeaders();
  const qp = new URLSearchParams();
  if (doel.wiSfId) qp.set('wiSfId', doel.wiSfId);
  else if (doel.woSfId) qp.set('woSfId', doel.woSfId);
  else return [];

  try {
    const res = await fetch(`/api/media?${qp.toString()}`, { headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success !== true || !Array.isArray(data.media)) return [];
    return data.media.map((m: Record<string, unknown>) => ({
      id: String(m.id),
      url: String(m.url),
      type: m.type === 'video' ? 'video' : 'foto',
      mimeType: (m.mime_type as string | null) ?? null,
      wiSfId: (m.wi_sf_id as string | null) ?? null,
      woSfId: (m.wo_sf_id as string | null) ?? null,
      fase: String(m.fase ?? ''),
      actorEmail: (m.actor_email as string | null) ?? null,
      takenAt: String(m.taken_at ?? m.created_at ?? ''),
    }));
  } catch {
    return [];
  }
}

export interface MediaCorrectie {
  mediaIds: string[];
  actie: 'verwijderen' | 'verplaatsen';
  reden: string;
  doelFase?: string;
  doelWiSfId?: string;
}

/**
 * Media corrigeren (verwijderen of verplaatsen). Faalt hard met een melding:
 * dit is een schrijfactie op wat de klant ziet, dus stil falen mag niet.
 */
export async function corrigeerMedia(
  correctie: MediaCorrectie,
): Promise<{ ok: boolean; melding: string }> {
  const headers = await authHeaders();
  try {
    const res = await fetch('/api/media/corrigeren', {
      method: 'POST',
      headers,
      body: JSON.stringify(correctie),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.success === true) {
      const n = data.gelukt ?? correctie.mediaIds.length;
      return {
        ok: true,
        melding:
          correctie.actie === 'verwijderen'
            ? `${n} item(s) verwijderd.`
            : `${n} item(s) verplaatst.`,
      };
    }
    const detail =
      typeof data.message === 'string'
        ? data.message
        : Array.isArray(data.resultaat)
          ? (data.resultaat.find((r: { fout?: string }) => r.fout)?.fout ?? 'onbekende fout')
          : `HTTP ${res.status}`;
    return { ok: false, melding: `Corrigeren mislukt: ${detail}` };
  } catch (err) {
    return {
      ok: false,
      melding: `Corrigeren mislukt: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}
